import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraBadge, SpecraButton, SpecraDetailPage, SpecraSelect, SpecraTag, SpecraText } from '@mono/ui-components';
import { SpecraFilterToolbar } from '@mono/ui-components/captains';
import { SpecraEmptyState, SpecraEntityCell, SpecraInlineBanner, SpecraPageHeader, SpecraRestrictedChip, SpecraSectionHeader, SpecraStatusPill, SpecraTabBar } from '@mono/ui-components/lieutenants';
import { Testids } from '@mono/ui-selectors';
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useFeatureFlag } from '../../../../contexts/featureFlagContext';
import { useSharePointAuth } from '../../../../contexts/sharePointAuthContext';
import { proposalHref } from '../../../../lib/proposalHref';
import { categoryLabel, DOCUMENT_CATEGORIES, DOCUMENT_SECTIONS, ORIGINS } from './categories';
import { LinkFromSharePointDrawer } from './LinkFromSharePointDrawer';
import { BringInDialog, EditDialog, LibraryDialog, RemoveDialog, UploadDialog } from './ProposalDocumentDialogs';
import { DocumentsRail, linkHost, linkTitle, VersionsDrawer } from './ProposalDocumentPanels';
import { documentErrorText, filtersActive, useProposalDocuments, useProposalDocumentWrites } from './useProposalDocuments';
const T = Testids.proposalDocuments;
const STATUS_LABEL = {
    processing: 'Processing',
    failed: 'Could not be processed',
    unreviewed_amendment: 'Amendment not reviewed',
    superseded: 'Replaced by a newer one'
};
function formatDate(iso) {
    const d = new Date(iso);
    return iso && !Number.isNaN(d.getTime()) ? d.toLocaleDateString('en-US') : '';
}
export function ProposalDocumentsPage() {
    const documentsHub = useFeatureFlag('proposal_documents_hub_v1') === true;
    const microsoft = useSharePointAuth();
    const { id } = useParams();
    const [params, setParams] = useSearchParams();
    const projectId = id ?? '';
    const filters = {
        query: params.get('q') ?? '',
        category: params.get('category') ?? '',
        origin: params.get('origin') ?? ''
    };
    const filtered = filtersActive(filters);
    // The unfiltered list feeds the section counts and the rail; the filtered one only the rows.
    const base = useProposalDocuments(projectId);
    const rows = useProposalDocuments(projectId, filters, filtered);
    const query = filtered ? rows : base;
    const writes = useProposalDocumentWrites(projectId);
    const [dialog, setDialog] = useState(null);
    const [search, setSearch] = useState(filters.query);
    const section = params.get('section') ?? 'all';
    const data = query.data;
    const setFilter = (key, value)=>setParams((prev)=>{
            const next = new URLSearchParams(prev);
            if (value) next.set(key, value);
            else next.delete(key);
            return next;
        }, {
            replace: true
        });
    // Typing commits to the URL after a pause; the URL is the source of truth for the query.
    useEffect(()=>{
        const t = setTimeout(()=>setParams((prev)=>{
                if ((prev.get('q') ?? '') === search) return prev;
                const next = new URLSearchParams(prev);
                if (search) next.set('q', search);
                else next.delete('q');
                return next;
            }, {
                replace: true
            }), 250);
        return ()=>clearTimeout(t);
    }, [
        search,
        setParams
    ]);
    const searchProps = {
        'aria-label': 'Search documents',
        placeholder: 'Search documents',
        'data-testid': T.search,
        value: search,
        onChange: (e)=>setSearch(e.target.value),
        onClear: ()=>setSearch('')
    };
    const clearFilters = ()=>{
        setSearch('');
        setParams((prev)=>{
            const next = new URLSearchParams(prev);
            for (const k of [
                'q',
                'category',
                'origin'
            ])next.delete(k);
            return next;
        }, {
            replace: true
        });
    };
    const bySection = useMemo(()=>{
        const out = {};
        for (const d of data?.documents ?? []){
            out[d.section] = [
                ...out[d.section] ?? [],
                d
            ];
        }
        return out;
    }, [
        data
    ]);
    const counts = useMemo(()=>Object.fromEntries((base.data?.sections ?? []).map((s)=>[
                s.section,
                s.count
            ])), [
        base.data
    ]);
    useEffect(()=>{
        if (!data || section === 'all') return;
        document.getElementById(`proposal-documents-${section}`)?.scrollIntoView?.({
            block: 'start'
        });
    }, [
        data,
        section
    ]);
    if (!id) return null;
    const canUpload = data?.canUpload === true;
    const canLinkSharePoint = documentsHub && canUpload && microsoft.isSharePointConnected && !microsoft.isLoading;
    const canUploadPricing = data?.canUploadPricingOnly === true;
    const pursuitRows = (bySection.pursuit ?? []).filter((d)=>d.artifactId !== '');
    const anyPending = Object.values(writes).some((m)=>m.isPending);
    const lastError = Object.values(writes).find((m)=>m.isError)?.error;
    const actions = data ? /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            canLinkSharePoint ? /*#__PURE__*/ _jsx(SpecraButton, {
                variant: "secondary",
                "data-testid": T.sharepointButton,
                onClick: ()=>{
                    writes.sharepoint.reset();
                    setDialog({
                        kind: 'sharepoint'
                    });
                },
                children: "Link from SharePoint"
            }) : null,
            canUpload ? /*#__PURE__*/ _jsx(SpecraButton, {
                variant: "secondary",
                "data-testid": T.libraryButton,
                onClick: ()=>setDialog({
                        kind: 'library'
                    }),
                children: "Import from Library"
            }) : null,
            canUpload || canUploadPricing ? /*#__PURE__*/ _jsx(SpecraButton, {
                "data-testid": T.uploadButton,
                onClick: ()=>setDialog({
                        kind: 'upload'
                    }),
                children: "Upload"
            }) : null
        ]
    }) : null;
    const select = (value)=>{
        const next = new URLSearchParams(params);
        if (value === 'all') next.delete('section');
        else next.set('section', value);
        setParams(next, {
            replace: true
        });
    };
    let body;
    if (query.isPending) {
        body = /*#__PURE__*/ _jsx("p", {
            "aria-busy": "true",
            "data-testid": T.loading,
            children: "Loading documents…"
        });
    } else if (query.isError) {
        body = /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
            tone: "danger",
            role: "alert",
            "data-testid": T.error,
            action: /*#__PURE__*/ _jsx(SpecraButton, {
                size: "sm",
                "data-testid": T.retry,
                onClick: ()=>query.refetch(),
                children: "Try again"
            }),
            children: [
                "Documents could not be loaded. ",
                documentErrorText(query.error)
            ]
        });
    } else if (!data || !filtered && data.total === 0 && data.documents.length === 0) {
        body = /*#__PURE__*/ _jsx(SpecraEmptyState, {
            "data-testid": T.pageEmpty,
            title: "No documents yet",
            description: canUpload || canUploadPricing ? 'Upload the solicitation, your pricing and anything else this proposal needs.' : 'There are no documents you can see on this proposal yet.',
            action: canUpload || canUploadPricing ? /*#__PURE__*/ _jsx(SpecraButton, {
                onClick: ()=>setDialog({
                        kind: 'upload'
                    }),
                children: "Upload"
            }) : undefined
        });
    } else {
        body = /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(SpecraFilterToolbar, {
                    search: searchProps,
                    filters: /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsxs(SpecraSelect, {
                                "aria-label": "Filter by category",
                                "data-testid": T.categoryFilter,
                                value: filters.category,
                                onChange: (e)=>setFilter('category', e.target.value),
                                children: [
                                    /*#__PURE__*/ _jsx("option", {
                                        value: "",
                                        children: "All categories"
                                    }),
                                    DOCUMENT_CATEGORIES.map((c)=>/*#__PURE__*/ _jsx("option", {
                                            value: c.value,
                                            children: c.label
                                        }, c.value))
                                ]
                            }),
                            /*#__PURE__*/ _jsxs(SpecraSelect, {
                                "aria-label": "Filter by origin",
                                "data-testid": T.originFilter,
                                value: filters.origin,
                                onChange: (e)=>setFilter('origin', e.target.value),
                                children: [
                                    /*#__PURE__*/ _jsx("option", {
                                        value: "",
                                        children: "Any origin"
                                    }),
                                    ORIGINS.map((o)=>/*#__PURE__*/ _jsx("option", {
                                            value: o.value,
                                            children: o.label
                                        }, o.value))
                                ]
                            }),
                            filtered ? /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: "ghost",
                                "data-testid": T.clearFilters,
                                onClick: clearFilters,
                                children: "Clear filters"
                            }) : null
                        ]
                    })
                }),
                filtered && data.documents.length === 0 ? /*#__PURE__*/ _jsx(SpecraEmptyState, {
                    "data-testid": T.noResults,
                    title: "No documents match",
                    description: "Try a different search, or clear the filters to see everything on this proposal.",
                    action: /*#__PURE__*/ _jsx(SpecraButton, {
                        onClick: clearFilters,
                        children: "Clear filters"
                    })
                }) : null,
                /*#__PURE__*/ _jsx(SpecraTabBar, {
                    label: "Document sections",
                    value: section,
                    onValueChange: select,
                    options: [
                        {
                            value: 'all',
                            label: 'All',
                            count: base.data?.total ?? data.total,
                            testId: T.tab
                        },
                        ...DOCUMENT_SECTIONS.map((s)=>({
                                value: s.key,
                                label: s.label,
                                count: counts[s.key] ?? 0,
                                testId: T.tab
                            }))
                    ]
                }),
                DOCUMENT_SECTIONS.map((s)=>{
                    const rows = bySection[s.key] ?? [];
                    if (filtered && rows.length === 0) return null;
                    return /*#__PURE__*/ _jsxs("section", {
                        id: `proposal-documents-${s.key}`,
                        "data-testid": T.section,
                        "data-section": s.key,
                        className: "space-y-2",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                                variant: "group",
                                title: s.label,
                                action: s.key === 'pursuit' && canUpload && pursuitRows.length > 0 ? /*#__PURE__*/ _jsx(SpecraButton, {
                                    size: "sm",
                                    variant: "secondary",
                                    "data-testid": T.bringInButton,
                                    onClick: ()=>setDialog({
                                            kind: 'bring-in'
                                        }),
                                    children: "Bring in from the pursuit"
                                }) : undefined
                            }),
                            rows.length === 0 ? /*#__PURE__*/ _jsx(SpecraEmptyState, {
                                size: "compact",
                                "data-testid": T.sectionEmpty,
                                title: s.empty
                            }) : /*#__PURE__*/ _jsx("ul", {
                                className: "divide-y",
                                children: rows.map((d)=>/*#__PURE__*/ _jsx(DocumentRow, {
                                        doc: d,
                                        projectId: projectId,
                                        canUpload: canUpload,
                                        busy: anyPending,
                                        onVersions: ()=>setDialog({
                                                kind: 'versions',
                                                doc: d
                                            }),
                                        onEdit: ()=>setDialog({
                                                kind: 'edit',
                                                doc: d
                                            }),
                                        onRemove: ()=>setDialog({
                                                kind: 'remove',
                                                doc: d
                                            }),
                                        onPackage: ()=>writes.setPackage.mutate({
                                                volumeDocumentId: d.volumeDocumentId,
                                                isSubmissionDoc: !d.inPackage
                                            })
                                    }, d.id))
                            })
                        ]
                    }, s.key);
                })
            ]
        });
    }
    const close = ()=>setDialog(null);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(SpecraDetailPage, {
                padding: "page",
                "data-testid": T.page,
                header: /*#__PURE__*/ _jsxs("div", {
                    className: "space-y-4",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraPageHeader, {
                            titleSize: "ui",
                            title: "Documents",
                            description: "Everything for this proposal in one place: what the government sent, what we are writing, and what we are pulling in.",
                            actions: actions
                        }),
                        lastError && !dialog ? /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                            tone: "danger",
                            role: "alert",
                            "data-testid": T.actionError,
                            children: documentErrorText(lastError)
                        }) : null
                    ]
                }),
                aside: base.data ? /*#__PURE__*/ _jsx(DocumentsRail, {
                    documents: base.data.documents
                }) : undefined,
                children: /*#__PURE__*/ _jsx("div", {
                    className: "space-y-4",
                    children: body
                })
            }),
            dialog?.kind === 'versions' ? /*#__PURE__*/ _jsx(VersionsDrawer, {
                projectId: projectId,
                doc: dialog.doc,
                writes: writes,
                onClose: close
            }) : null,
            dialog?.kind === 'sharepoint' && canLinkSharePoint ? /*#__PURE__*/ _jsx(LinkFromSharePointDrawer, {
                writes: writes,
                onClose: close
            }) : null,
            dialog?.kind === 'upload' ? /*#__PURE__*/ _jsx(UploadDialog, {
                writes: writes,
                canUpload: canUpload,
                onClose: close
            }) : null,
            dialog?.kind === 'library' ? /*#__PURE__*/ _jsx(LibraryDialog, {
                writes: writes,
                onClose: close
            }) : null,
            dialog?.kind === 'bring-in' ? /*#__PURE__*/ _jsx(BringInDialog, {
                writes: writes,
                candidates: pursuitRows,
                onClose: close
            }) : null,
            dialog?.kind === 'edit' ? /*#__PURE__*/ _jsx(EditDialog, {
                writes: writes,
                doc: dialog.doc,
                canUpload: canUpload,
                onClose: close
            }) : null,
            dialog?.kind === 'remove' ? /*#__PURE__*/ _jsx(RemoveDialog, {
                writes: writes,
                doc: dialog.doc,
                onClose: close
            }) : null
        ]
    });
}
function DocumentRow(p) {
    const d = p.doc;
    const isVolume = d.store === 'volume_document' && d.volumeId !== '';
    // Links to other places (SharePoint links, pursuit/Library candidates) have no
    // editable title or category here; only stored files do.
    const editable = d.canEdit && d.store === 'attachment';
    if (d.store === 'sharepoint_link' && !d.externalUrl) {
        return /*#__PURE__*/ _jsxs("li", {
            className: "space-y-2 py-2",
            "data-testid": T.row,
            "data-document-id": d.id,
            children: [
                /*#__PURE__*/ _jsx(SpecraEntityCell, {
                    title: d.title
                }),
                /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                    "data-testid": T.sharepointAccess,
                    tone: "info",
                    children: "Request access in SharePoint"
                })
            ]
        });
    }
    return /*#__PURE__*/ _jsxs("li", {
        className: "flex flex-wrap items-center gap-3 py-2",
        "data-testid": T.row,
        "data-document-id": d.id,
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "min-w-0 flex-1",
                children: d.store === 'sharepoint_link' && d.externalUrl ? /*#__PURE__*/ _jsx(SpecraEntityCell, {
                    title: /*#__PURE__*/ _jsx("a", {
                        "data-testid": T.linkTitle,
                        className: "underline",
                        href: d.externalUrl,
                        target: "_blank",
                        rel: "noopener noreferrer",
                        children: linkTitle(d)
                    }),
                    description: linkHost(d) || d.description
                }) : /*#__PURE__*/ _jsx(SpecraEntityCell, {
                    title: d.title,
                    description: d.filename && d.filename !== d.title ? d.filename : d.description
                })
            }),
            d.category ? /*#__PURE__*/ _jsx(SpecraTag, {
                children: categoryLabel(d.category)
            }) : null,
            d.versionLabel ? /*#__PURE__*/ _jsx(SpecraBadge, {
                children: d.versionLabel
            }) : null,
            /*#__PURE__*/ _jsx(SpecraText, {
                size: "meta",
                children: [
                    d.originLabel,
                    formatDate(d.updatedAt)
                ].filter(Boolean).join(' · ')
            }),
            d.inPackage ? /*#__PURE__*/ _jsx(SpecraStatusPill, {
                status: "success",
                children: "In package"
            }) : null,
            STATUS_LABEL[d.status] ? /*#__PURE__*/ _jsx(SpecraStatusPill, {
                status: "warning",
                children: STATUS_LABEL[d.status]
            }) : null,
            d.sensitivity === 'legal_restricted' ? /*#__PURE__*/ _jsx(SpecraRestrictedChip, {
                "data-testid": T.restricted,
                label: "Restricted"
            }) : null,
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center gap-1",
                children: [
                    isVolume ? /*#__PURE__*/ _jsx(Link, {
                        "data-testid": T.open,
                        className: "text-sm underline",
                        to: `${proposalHref(p.projectId, `volume/${encodeURIComponent(d.volumeId)}`)}?doc=${encodeURIComponent(d.volumeDocumentId)}`,
                        children: "Open"
                    }) : null,
                    isVolume && d.shirohigeDocumentId ? /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        variant: "ghost",
                        "data-testid": T.versionsButton,
                        onClick: p.onVersions,
                        children: "Versions"
                    }) : null,
                    isVolume && d.canEdit ? /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        variant: "ghost",
                        "data-testid": T.packageButton,
                        disabled: p.busy,
                        onClick: p.onPackage,
                        children: d.inPackage ? 'Take out of package' : 'Mark as package file'
                    }) : null,
                    editable ? /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        variant: "ghost",
                        "data-testid": T.editButton,
                        disabled: p.busy,
                        onClick: p.onEdit,
                        children: "Change details"
                    }) : null,
                    d.canRemove ? /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        variant: "ghost",
                        "data-testid": T.removeButton,
                        disabled: p.busy,
                        onClick: p.onRemove,
                        children: "Remove"
                    }) : null
                ]
            })
        ]
    });
}
